import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { AUDIT_TRAIL_DETAIL_PANEL_HEIGHT } from './constants/constants';
import {
	formatActedAsDisplayName,
	formatEventDateCell,
	getEventDisplayText,
	getGroupedChildEventText,
	getGroupedEventDisplayText,
	getQuoteTotalDisplay,
	isImpersonatedSessionRow,
	parseAuditTrailChildRow,
	LEGACY_ONE_TIME_TOTAL_TOOLTIP
} from './auditTrailUtils';

const EMPTY_LABEL = '[empty]';
const DETAIL_COLUMN_MIN_WIDTH = 220;
const SINGLE_DETAIL_PANEL_PADDING = 32;
const SINGLE_DETAIL_FIELD_HEIGHT = 76;
const SINGLE_DETAIL_FULL_WIDTH_BASE = 56;
const SINGLE_DETAIL_LINE_HEIGHT = 20;
const SINGLE_DETAIL_CHARS_PER_LINE = 72;
const SINGLE_DETAIL_PANEL_MAX_HEIGHT = 360;

const detailPanelBaseSx = {
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	boxSizing: 'border-box',
	p: 2
};

const groupedDetailPanelContainerSx = {
	...detailPanelBaseSx,
	maxHeight: AUDIT_TRAIL_DETAIL_PANEL_HEIGHT,
	overflow: 'auto'
};

const singleDetailPanelContainerSx = {
	...detailPanelBaseSx
};

const detailGridSx = (fieldCount) => ({
	display: 'grid',
	gridTemplateColumns: fieldCount === 1
		? '1fr'
		: `repeat(auto-fit, minmax(${DETAIL_COLUMN_MIN_WIDTH}px, 1fr))`,
	gap: 1.5,
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	boxSizing: 'border-box'
});

const DetailField = ({ label, value, spanFullWidth = false }) => {
	const displayValue =
		value === null || value === undefined || value === ''
			? EMPTY_LABEL
			: String(value);

	return (
		<Box
			sx={(theme) => ({
				minWidth: 0,
				width: '100%',
				boxSizing: 'border-box',
				py: 1,
				px: 1.5,
				borderRadius: 1,
				borderLeft: '3px solid',
				borderLeftColor: theme.palette.primary.main,
				backgroundColor: theme.palette.mode === 'dark'
					? 'rgba(255, 255, 255, 0.04)'
					: 'rgba(0, 0, 0, 0.03)',
				color: 'text.primary',
				...(spanFullWidth && { gridColumn: '1 / -1' })
			})}
		>
			<Typography
				variant="caption"
				sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 600 }}
			>
				{label}
			</Typography>
			<Typography
				component="div"
				variant="body2"
				sx={{
					display: 'block',
					color: 'text.primary',
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					overflowWrap: 'anywhere',
					width: '100%',
					maxWidth: '100%',
					minWidth: 0
				}}
			>
				{displayValue}
			</Typography>
		</Box>
	);
};

const GroupedEventEntry = ({ child, formatCurrency }) => {
	const childRow = parseAuditTrailChildRow(child);
	const eventText = getGroupedChildEventText(child);
	const eventDate = formatEventDateCell(childRow?.EventDate)?.display || '';
	const quoteTotalDisplay = getQuoteTotalDisplay(childRow, formatCurrency);

	return (
		<Box
			sx={(theme) => ({
				minWidth: 0,
				width: '100%',
				boxSizing: 'border-box',
				py: 1,
				px: 1.5,
				borderRadius: 1,
				borderLeft: '3px solid',
				borderLeftColor: theme.palette.primary.main,
				backgroundColor: theme.palette.mode === 'dark'
					? 'rgba(255, 255, 255, 0.04)'
					: 'rgba(0, 0, 0, 0.03)'
			})}
		>
			<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }}>
				{eventDate || EMPTY_LABEL}
			</Typography>
			{eventText && (
				<Typography
					variant="body2"
					sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}
				>
					{eventText}
				</Typography>
			)}
			{quoteTotalDisplay.value && (
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
					{quoteTotalDisplay.isLegacyOneTimeOnly
						? `One-time total: ${quoteTotalDisplay.value}`
						: `Total quote value: ${quoteTotalDisplay.value}`}
				</Typography>
			)}
		</Box>
	);
};

const buildDetailFields = (row, formatCurrency) => {
	const fields = [];
	const eventText = getEventDisplayText(row);
	const quoteTotalDisplay = getQuoteTotalDisplay(row, formatCurrency);

	if (eventText) {
		fields.push({ label: 'Event details', value: eventText, spanFullWidth: true });
	}

	if (isImpersonatedSessionRow(row)) {
		fields.push({ label: 'Acting as', value: formatActedAsDisplayName(row) });
	}

	if (row?.QuoteStatus) {
		fields.push({ label: 'Status', value: row.QuoteStatus });
	}

	if (row?.InvoicePostStatus) {
		fields.push({ label: 'Post', value: row.InvoicePostStatus });
	}

	if (row?.OrderPorterApproved !== null && row?.OrderPorterApproved !== undefined) {
		fields.push({
			label: 'Approved',
			value: row.OrderPorterApproved ? 'True' : 'False'
		});
	}

	if (quoteTotalDisplay.value) {
		fields.push({
			label: quoteTotalDisplay.isLegacyOneTimeOnly
				? 'One-time total at event'
				: 'Total quote value at event',
			value: quoteTotalDisplay.isLegacyOneTimeOnly
				? `${quoteTotalDisplay.value} (${LEGACY_ONE_TIME_TOTAL_TOOLTIP})`
				: quoteTotalDisplay.value
		});
	}

	return fields;
};

export const getQuoteAuditDetailPanelHeight = (row, formatCurrency = () => '') => {
	if (!row) {
		return 72;
	}

	const isMultiEventGroup =
		row.IsGrouped
		&& (row.GroupedCount > 1 || (row.GroupedEvents?.length ?? 0) > 1);

	if (isMultiEventGroup) {
		return AUDIT_TRAIL_DETAIL_PANEL_HEIGHT;
	}

	const detailFields = buildDetailFields(row, formatCurrency);

	if (!detailFields.length) {
		return 72;
	}

	let height = SINGLE_DETAIL_PANEL_PADDING;
	const gridFields = detailFields.filter((field) => !field.spanFullWidth);
	const fullWidthFields = detailFields.filter((field) => field.spanFullWidth);
	const gridRows = Math.max(1, Math.ceil(gridFields.length / 2));

	height += gridRows * SINGLE_DETAIL_FIELD_HEIGHT;

	fullWidthFields.forEach((field) => {
		const text = String(field.value ?? '');
		const textLines = Math.max(1, Math.ceil(text.length / SINGLE_DETAIL_CHARS_PER_LINE));
		height += SINGLE_DETAIL_FULL_WIDTH_BASE + textLines * SINGLE_DETAIL_LINE_HEIGHT;
	});

	return Math.max(72, Math.min(height, SINGLE_DETAIL_PANEL_MAX_HEIGHT));
};

export const QuoteAuditChangeDetails = ({ row, formatCurrency }) => {
	if (!row) {
		return null;
	}

	if (row.IsGrouped && row.GroupedEvents?.length > 1) {
		return (
			<Box sx={groupedDetailPanelContainerSx}>
				<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, fontWeight: 600 }}>
					{getGroupedEventDisplayText(row)}
				</Typography>
				<Box sx={{ ...detailGridSx(1), gap: 1 }}>
					{row.GroupedEvents.map((child, index) => {
						const childRow = parseAuditTrailChildRow(child);

						return (
							<Box key={childRow?.IdQuoteAuditTrail || index}>
								{index > 0 && <Divider sx={{ my: 1 }} />}
								<GroupedEventEntry child={child} formatCurrency={formatCurrency} />
							</Box>
						);
					})}
				</Box>
			</Box>
		);
	}

	const detailFields = buildDetailFields(row, formatCurrency);

	if (!detailFields.length) {
		return null;
	}

	return (
		<Box sx={singleDetailPanelContainerSx}>
			<Box sx={detailGridSx(detailFields.length)}>
				{detailFields.map((field) => (
					<DetailField
						key={field.label}
						label={field.label}
						value={field.value}
						spanFullWidth={field.spanFullWidth}
					/>
				))}
			</Box>
		</Box>
	);
};
